import { CarerRole, type Home, type User } from '@lmc/ts-common'
import { getNominative } from 'common/utils'
import { get } from 'lodash'
import { isBrowser, isMobile, isTablet } from 'react-device-detect'
import { history } from '../../store'

export const getReportSubTabs = (
  features: Home,
  carePlanNominative: string,
  downloadCentreEnabled?: boolean
) => {
  let tabs = [
    { key: 'coronavirus', label: 'Coronavirus' },
    { key: 'monitoring/fluids', label: 'Fluids Overview' },
    { key: 'monitoring/meals', label: 'Food Overview' },
    { key: 'incidents', label: 'Incidents' },
    { key: 'handovers/current', label: 'Current Handover' },
    { key: 'handovers/history', label: 'Handover History' },
    {
      key: 'care-plans',
      label: `${getNominative(carePlanNominative, 'Care Plans')}`
    }
  ]

  if (features?.features?.skillsAndNeeds?.enabled) {
    tabs = [
      ...tabs,
      { key: 'training-matrix', label: 'Training and skills matrix' }
    ]
  }

  // TODO replace with feature object
  if (features?.features?.pro?.enabled) {
    tabs = [
      ...tabs,
      {
        key: 'care-planning/risk-assessments',
        label: 'Risk Assessment Scores'
      }
    ]
  }

  if (features?.features?.eMAR?.enabled) {
    if (features?.features?.eMARStock?.enabled) {
      tabs = [
        ...tabs,
        ...[
          { key: 'emar/medication-rounds', label: 'Medication Rounds' },
          { key: 'emar/stock-discrepancies', label: 'Stock Discrepancies' },
          { key: 'emar/stock-levels', label: 'Stock Levels' }
        ]
      ]
    } else {
      tabs = [
        ...tabs,
        { key: 'emar/medication-rounds', label: 'Medication Rounds' }
      ]
    }
  }

  if (downloadCentreEnabled) {
    tabs = [...tabs, { key: 'download-centre', label: 'Download centre' }]
  }

  tabs = [...tabs, { key: 'cqi', label: 'CQI' }]

  return tabs
}

export const getProfileMenuItems = (
  user: User & { privacyOfficer?: boolean },
  privacyOfficerEnabled?: boolean
) => {
  const isPrivacyOfficer = user?.privacyOfficer

  let menuItems = [
    {
      type: 'divider'
    },
    {
      value: 'manage-account',
      label: 'Account Security'
    },
    {
      value: 'signout',
      label: 'Sign out'
    }
  ]

  if (privacyOfficerEnabled) {
    if (isPrivacyOfficer) {
      menuItems = [
        {
          value: 'admin-view',
          label: 'Admin View'
        },
        ...menuItems
      ]
    } else {
      menuItems = [
        {
          value: 'privacy-officer-view',
          label: 'Privacy Officer View'
        },
        ...menuItems
      ]
    }
  }

  if (get(user, 'homes.length') > 1) {
    menuItems = [
      {
        value: 'switch',
        label: 'Switch Site'
      },
      ...menuItems
    ]
  }

  if (user && user.role === CarerRole.careHomeAdmin) {
    menuItems.unshift({
      value: `organisation/team/${user.id}/profile`,
      label: 'Profile'
    })
  }

  return menuItems
}

export const getSections = (
  user: User,
  features: Home,
  preferredPeopleNominative: string,
  carePlanNominative: string,
  handleSignOut?: () => void,
  downloadCentreEnabled?: boolean,
  hasRota?: boolean,
  goalsEnabled?: boolean,
  hasRolePermissionForSeniorCarer?: boolean,
  flagRosteringPaywallScreenEnabled?: boolean
) => {
  const deviceAccessControlGranted =
    features?.features?.deviceAccessControl?.granted

  let allTabs = [
    {
      key: 'list',
      label: 'Team Members'
    },
    {
      key: 'shift-passwords',
      label: 'Shift Passwords',
      disabledByPermission: hasRolePermissionForSeniorCarer
    },
    {
      key: 'documents',
      label: 'Documents',
      disabledByPermission: hasRolePermissionForSeniorCarer
    }
  ]

  allTabs = [
    ...allTabs,
    ...(deviceAccessControlGranted
      ? [
          {
            key: 'devices',
            label: 'Devices',
            disabledByPermission: hasRolePermissionForSeniorCarer
          }
        ]
      : []),
    ...(hasRota
      ? [
          {
            key: 'all-absences',
            label: 'All Absences',
            disabledByPermission: hasRolePermissionForSeniorCarer
          }
        ]
      : [])
  ]

  return [
    {
      key: 'organisation',
      label: 'Team',
      roles: [
        'carehome-admin',
        'group-admin',
        ...(hasRolePermissionForSeniorCarer ? ['senior-carer'] : [])
      ],
      subTabs: allTabs,
      isWeb: true
    },
    {
      key: 'homes',
      label: 'Sites',
      roles: ['group-admin', 'group-manager'],
      subTabs: [
        { label: 'Profile', key: 'profile' },
        { label: 'Charts', key: 'dashboard' }
      ],
      isWeb: true
    },
    {
      key: 'residents',
      label: getNominative(preferredPeopleNominative, 'Residents'),
      roles: ['carehome-admin', 'senior-carer'],
      isWeb: true
    },
    {
      key: 'logs',
      label: 'Logs',
      roles: ['carehome-admin', 'senior-carer'],
      url: 'logs',
      isWeb: true
    },
    {
      key: 'todos',
      label: "To-Do's",
      roles: ['carehome-admin', 'senior-carer'],
      subTabs: [
        {
          key: 'dashboard',
          label: 'Daily Dashboard'
        },
        {
          key: 'manage',
          label: "Manage To-Do's"
        }
      ],
      isWeb: true
    },
    ...((hasRota || flagRosteringPaywallScreenEnabled) &&
    features?.features?.pro?.enabled
      ? [
          {
            id: 'rota-menu-item',
            key: 'rota',
            label: 'Rota',
            roles: ['carehome-admin', 'senior-carer'],
            subTabs: [
              // {
              //   key: 'published',
              //   label: 'Published Rota',
              // },
              {
                key: 'builder',
                label: 'Rota Builder'
              },
              {
                key: 'patterns',
                label: 'Manage Patterns'
              }
            ],
            isWeb: true
          }
        ]
      : []),

    {
      key: 'reports',
      label: 'Reports',
      roles: ['carehome-admin', 'senior-carer'],
      subTabs: getReportSubTabs(
        features,
        carePlanNominative,
        downloadCentreEnabled
      ),
      isWeb: true
    },
    {
      key: 'group-reports',
      label: 'Group Reports',
      roles: ['group-admin', 'group-manager'],
      isWeb: true,
      subTabs: [
        { label: 'BMI', key: '/' },
        { label: 'To-Do’s', key: 'todos' },
        { label: 'Incidents', key: 'incidents' },
        { label: 'Care Plans', key: 'care-plans' },
        ...(downloadCentreEnabled
          ? [{ label: 'Download Centre', key: 'download-centre' }]
          : []),
        ...(goalsEnabled
          ? [{ label: 'Goals Overview', key: 'goals-overview' }]
          : [])
      ]
    },
    {
      key: `organisation/team/${user?.id}/profile`,
      label: 'Profile',
      roles: ['group-admin', 'group-manager', 'carehome-admin', 'senior-carer'],
      isWeb: !isBrowser
    },
    {
      key: 'help',
      label: 'Help Centre',
      roles: ['carehome-admin'],
      isWeb: isTablet || isMobile
    },
    {
      key: 'signout',
      label: 'Sign out',
      action: handleSignOut,
      roles: ['group-admin', 'group-manager', 'carehome-admin', 'senior-carer'],
      isWeb: isTablet || isMobile
    }
  ]
}

export const isNoticeboardGranted = (features: Home) =>
  features?.features?.noticeBoard?.granted &&
  features?.features?.noticeBoard?.enabled &&
  features?.pro

export const ALLOWED_ROUTES = [
  '/',
  '/logs',
  '/logs/[id]',
  '/organisation/documents',
  '/organisation/list',
  '/organisation/shift-passwords',
  '/organisation/all-absences',
  '/organisation/team/[id]/profile',
  '/organisation/team/[id]/profile/edit',
  '/residents/[id]/care-plans',
  '/residents/[id]/care-plans/[id]',
  '/residents/[id]/care-plans/[id]/edit',
  '/residents/[id]/care-plans/[id]/history',
  '/residents/[id]/care-plans/[id]/history/[id]',
  '/residents/[id]/consent',
  '/residents/[id]/consent/[id]',
  '/residents/[id]/daily-report',
  '/residents/[id]/documents',
  '/residents/[id]/emar',
  '/residents/[id]/emar/[id]',
  '/residents/[id]/emar/[id]/stock',
  '/residents/[id]/emar/stock',
  '/residents/[id]/goals/',
  '/residents/[id]/goals/create',
  '/residents/[id]/goals/[id]',
  '/residents/[id]/initial-assessments',
  '/residents/[id]/profile',
  '/residents/[id]/risk-assessments',
  '/residents/[id]/risk-assessments/[id]',
  '/residents/[id]/risk-assessments/[id]/history',
  '/residents/[id]/risk-assessments/[id]/history/[id]',
  '/residents/list',
  '/settings/billing',
  '/settings/consent-templates',
  '/settings/consent-templates/[id]',
  '/settings/family-settings',
  '/settings/skills-and-needs',
  '/settings/skills-and-needs/skill-templates',
  '/settings/skills-and-needs/need-templates',
  '/settings/zones',
  '/help'
]

export const isRouteSpecificPath = (path: string) =>
  history.location.pathname.startsWith(path)
